import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, Users, Image as ImageIcon, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AuthImage from "@/components/AuthImage";

/**
 * Create a new event. Form fields per spec:
 *   • title (required)
 *   • location (required)
 *   • date + time (required) — combined into ISO 8601 before submit
 *   • capacity (required, ≥ 1)
 *   • description (optional)
 *   • cover image (optional)
 * Creator is auto-RSVPed by the backend.
 */
function defaultDate() {
  const d = new Date(Date.now() + 86400000); // tomorrow same time
  d.setSeconds(0, 0);
  return d;
}

function pad(n) {
  return String(n).padStart(2, "0");
}

export default function ComposeEvent() {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const def = useRef(defaultDate());
  const [dateStr, setDateStr] = useState(
    `${def.current.getFullYear()}-${pad(def.current.getMonth() + 1)}-${pad(def.current.getDate())}`,
  );
  const [timeStr, setTimeStr] = useState(`${pad(def.current.getHours())}:${pad(def.current.getMinutes())}`);
  const [capacity, setCapacity] = useState(10);
  const [description, setDescription] = useState("");
  const [imagePath, setImagePath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  // Ensure mobile spinner-style number input doesn't lose focus on submit.
  useEffect(() => undefined, []);

  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setErr("Only image files are allowed.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      setErr("Image too large (max 8MB).");
      return;
    }
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/upload", fd);
      setImagePath(r.data.path);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!title.trim() || !location.trim()) {
      setErr("Title and location are required.");
      return;
    }
    const local = new Date(`${dateStr}T${timeStr}:00`);
    if (Number.isNaN(local.getTime()) || local.getTime() < Date.now()) {
      setErr("Pick a future date and time.");
      return;
    }
    const capInt = parseInt(String(capacity), 10);
    if (!Number.isFinite(capInt) || capInt < 1) {
      setErr("Capacity must be a positive integer.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/events", {
        title: title.trim(),
        location: location.trim(),
        starts_at: local.toISOString(),
        capacity: capInt,
        description: description.trim(),
        image_path: imagePath || null,
      });
      nav(`/events/${r.data.id}`, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto fade-up">
      <button
        onClick={() => nav(-1)}
        className="text-white/60 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1 mb-6"
        data-testid="event-new-back"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> Back
      </button>

      <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em]">New event · 创建活动</p>
      <h1 className="font-serif text-4xl mt-2 mb-8">Host a moment.</h1>

      <form onSubmit={submit} className="space-y-6" data-testid="event-new-form">
        <div>
          <label className="text-xs uppercase tracking-widest text-white/40">Title</label>
          <input
            type="text"
            className="editorial-input mt-2"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={120}
            placeholder="Sunday Pickleball at TPC"
            data-testid="event-new-title"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-2">
              <Calendar size={12} strokeWidth={1.5} /> Date
            </label>
            <input
              type="date"
              className="editorial-input mt-2"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              required
              data-testid="event-new-date"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/40">Time</label>
            <input
              type="time"
              className="editorial-input mt-2"
              value={timeStr}
              onChange={(e) => setTimeStr(e.target.value)}
              required
              data-testid="event-new-time"
            />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-2">
            <MapPin size={12} strokeWidth={1.5} /> Location · 地点
          </label>
          <input
            type="text"
            className="editorial-input mt-2"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            required
            maxLength={200}
            placeholder="Tropicana Golf Club, Petaling Jaya"
            data-testid="event-new-location"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-2">
            <Users size={12} strokeWidth={1.5} /> Capacity · 人数上限
          </label>
          <input
            type="number"
            min={1}
            className="editorial-input mt-2"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            required
            data-testid="event-new-capacity"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-white/40">Description (optional)</label>
          <textarea
            rows={3}
            className="editorial-input mt-2 resize-none"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={1000}
            placeholder="What to bring, dress code, post-event dinner…"
            data-testid="event-new-description"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-2">
            <ImageIcon size={12} strokeWidth={1.5} /> Cover photo (optional)
          </label>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPickImage}
            data-testid="event-new-cover-input"
          />
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="border border-white/15 hover:border-[#d4af37]/60 text-white/70 hover:text-white px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50"
              data-testid="event-new-cover-pick"
            >
              {uploading ? "Uploading…" : imagePath ? "Replace cover" : "Choose image"}
            </button>
            {imagePath && (
              <div className="relative">
                <AuthImage path={imagePath} className="h-16 w-24 object-cover rounded border border-white/10" />
                <button
                  type="button"
                  onClick={() => setImagePath("")}
                  className="absolute -top-1.5 -right-1.5 bg-black/80 border border-white/20 rounded-full p-0.5 text-white/80 hover:text-white"
                  data-testid="event-new-cover-remove"
                >
                  <X size={12} strokeWidth={2} />
                </button>
              </div>
            )}
          </div>
        </div>

        {err && <p className="text-sm text-red-400" data-testid="event-new-error">{err}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-[#d4af37] text-black py-4 uppercase tracking-[0.2em] text-sm font-medium hover:bg-[#f3e5ab] transition disabled:opacity-50"
          data-testid="event-new-submit"
        >
          {submitting ? "Creating…" : "Publish event"}
        </button>
      </form>
    </div>
  );
}
